// Ad origin of the visit (UTM parameters and the Meta click ID). Captured at
// boot, before redirects drop the query string. Writing it to the device needs
// marketing consent (TDDDG § 25), so until then it lives only in memory.

import { CONSENT_VERSION, readConsent } from './consent';
import { isExcludedPath } from './scope';

const ATTRIBUTION_STORAGE_KEY = 'novofon_attribution';
// Meta keeps a click for 90 days (lifetime of the _fbc cookie).
const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmKey = (typeof UTM_KEYS)[number];

interface Attribution extends Partial<Record<UtmKey, string>> {
  fbclid?: string;
  landing_path: string;
  /** First visit with these parameters, milliseconds since the epoch */
  first_seen_at: number;
}

/** The `marketing` field of the registration and organization requests. */
export interface MarketingPayload extends Partial<Record<UtmKey, string>> {
  /** null: no decision yet, the server keeps what it has */
  consent: boolean | null;
  consent_version: string;
  landing_path?: string;
  fbclid?: string;
  first_seen_at?: number;
  fbp?: string;
  fbc?: string;
}

let current: Attribution | null = null;

const loadStored = (now: number): Attribution | null => {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Attribution;
    if (
      typeof value.landing_path !== 'string' ||
      typeof value.first_seen_at !== 'number' ||
      now - value.first_seen_at > ATTRIBUTION_MAX_AGE_MS
    ) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
};

/**
 * Reads the ad parameters of the entry URL. Only a URL with UTM parameters or a
 * click ID starts a new set; otherwise the stored set of a consenting visitor
 * is kept, so a reload never replaces the landing page.
 */
export const captureAttribution = (
  location: Pick<Location, 'pathname' | 'search'> = window.location,
  now = Date.now()
): void => {
  const consented = readConsent(now)?.marketing === true;
  current = consented ? loadStored(now) : null;
  if (isExcludedPath(location.pathname)) return;

  const params = new URLSearchParams(location.search);
  const captured: Attribution = { landing_path: location.pathname, first_seen_at: now };
  let found = false;
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      captured[key] = value;
      found = true;
    }
  }
  const fbclid = params.get('fbclid');
  if (fbclid) {
    captured.fbclid = fbclid;
    found = true;
  }
  if (!found) return;

  current = captured;
  if (consented) persistAttribution();
};

/** Keeps the set across reloads; call it only with marketing consent. */
export const persistAttribution = (): void => {
  if (!current) return;
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Storage unavailable: the set stays in memory.
  }
};

/** Removes the stored set when consent is withdrawn; the memory copy stays. */
export const forgetAttribution = (): void => {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
};

export const readCookie = (name: string): string | undefined => {
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return rest.join('=');
  }
  return undefined;
};

/**
 * UTM parameters and the landing page always (technical data); the click ID
 * and the Pixel cookies only with marketing consent. The cookies are sent in
 * the body because the API is on another origin and never sees them.
 */
export const getMarketingPayload = (): MarketingPayload => {
  const decision = readConsent();
  const payload: MarketingPayload = {
    consent: decision ? decision.marketing : null,
    consent_version: CONSENT_VERSION,
  };
  if (current) {
    for (const key of UTM_KEYS) {
      if (current[key]) payload[key] = current[key];
    }
    payload.landing_path = current.landing_path;
  }
  if (!decision?.marketing) return payload;

  if (current) payload.first_seen_at = current.first_seen_at;
  if (current?.fbclid) payload.fbclid = current.fbclid;
  const fbp = readCookie('_fbp');
  if (fbp) payload.fbp = fbp;
  // The Pixel sets _fbc only when it sees the click ID in the URL, which is
  // gone after the first redirect: build it from the captured click then.
  const fbc =
    readCookie('_fbc') ||
    (current?.fbclid ? `fb.1.${current.first_seen_at}.${current.fbclid}` : undefined);
  if (fbc) payload.fbc = fbc;
  return payload;
};
